import "./_review.scss";
import React, { useContext, useEffect, useRef, useState } from "react";
// context
import { UserContext } from "@src/App";
// api, interface
import { TWriteType } from "./Intreface";
import {
  deleteCommunityComment,
  getAnonymousNick,
  ICommunityCommentListItem,
} from "@src/api/community/community";
// provider data
import { ReviewProviderData } from "./Provider";
import { useReviewActions } from "./useReviewActions";
// components
import Modal from "@src/components/common/modal/Modal";
import { ImgUpload, ImgProvider, ImgPreview } from "@src/shared/src";
import Checkbox from "../../common/checkbox/Checkbox";
import Blank from "../../common/blank/Blank";
import { AutoResizeTextArea } from "@src/components/common/textArea/AutoResizeTextArea";
// images
import IconCommentImg from "@assets/images/common/community/icon-comment-img@3x.png";
import IconCommentAnonymous from "@assets/images/common/community/icon-comment-anonymous@3x.png";
import IconCommentLock from "@assets/images/common/community/icon-comment-lock@3x.png";

// 리뷰 작성 코멘트 영역
export const ReviewCommentWrite = ({
  isCommentModeCheck,
  scrollComponent,
}: {
  isCommentModeCheck: (status: boolean) => void;
  scrollComponent: {
    wrap: HTMLDivElement;
    contents: HTMLDivElement;
    actions: HTMLDivElement;
    calcProperties: () => void;
  };
}) => {
  // iOS 체크
  const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  // provider data
  const {
    reviewDataDetail,
    setReviewDataDetail,
    commentList,
    setCommentList,
    setSpecificComment,
    specificComment,
  } = useContext(ReviewProviderData).data;

  // user
  const user = useContext(UserContext);

  // useReviewActions
  const { writeComment } = useReviewActions();

  // 코멘트 작성 중 상태
  const [isCommentMode, setIsCommentMode] = useState<boolean>(false);

  // 코멘트 영역 이동 트리거
  const [scrollTrigger, setScrollTrigger] = useState<boolean>(false);
  const scrollTriggerRef = useRef<boolean>(false);

  // 비밀글, 익명글, 원댓글 id
  const [secret, setSecret] = useState<boolean>(false);
  const [anonymous, setAnonymous] = useState<boolean>(false);
  const [anonymousNick, setAnonymousNick] = useState<string>("");
  const [upperId, setUpperId] = useState<number>(0);

  // 댓글 내용
  const [value, setValue] = useState<string>("");

  // 댓글 textarea ref, comment 전체 영역 ref
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const commentRef = useRef<HTMLDivElement>(null);

  // 댓글창 높이
  const [commentHeight, setCommentHeight] = useState<number>(0);

  // textArea keydown 이벤트 추적
  const textAreaRefKeyDown = useRef("");

  // 클릭 이벤트 타겟 추적
  const documentClickTarget = useRef<HTMLElement | null>(null);

  // 댓글창(키보드) 열림/닫힘 타이밍 추적
  const resizeTimer = useRef<NodeJS.Timeout | null>(null);

  // 기본 창 크기(높이), 리사이즈 창 크기(높이)
  const defaultHeight = useRef<number>(0);
  const resizeHeight = useRef<number>(0);

  // visualViewport 이벤트 추적
  const visualViewportDefault = useRef(0);
  const visualViewportTimer = useRef<NodeJS.Timeout | null>(null);
  const chkVisualViewport = useRef(false);

  // 이미지 업로드
  const [imgUpload, setImgUpload] = useState<string[]>([]);
  const [imgFiles, setImgFiles] = useState<File[]>([]);
  const [imgBlobs, setImgBlobs] = useState<Blob[]>([]);
  const imgUploadFlag = useRef<boolean>(false);
  const imgDataRef = useRef<{
    blob: Blob[];
    file: File[];
    url: string[];
  }>(null);

  useEffect(() => {
    imgDataRef.current = {
      blob: imgBlobs,
      file: imgFiles,
      url: imgUpload,
    };
  }, [imgUpload, imgFiles, imgBlobs]);

  // 코멘트창 높이 조절
  const ro = new ResizeObserver((entries) => {
    for (let entry of entries) {
      setCommentHeight(entry.contentRect.height);
    }
  });

  // 댓글 작성 모드 활성화
  const commentActiveHandler = ({
    needToMove = false,
    isCommentMode = false,
  }: {
    needToMove: boolean;
    isCommentMode?: boolean;
  }) => {
    if (needToMove) {
      if (isCommentMode) {
        if (textAreaRef.current) textAreaRef.current.focus();
        commentRef.current?.closest(".review-detail-modal")?.classList.add("focus");
        scrollTriggerRef.current = true;
        setScrollTrigger(true);
      } else {
        if (textAreaRef.current) textAreaRef.current.focus();
        commentRef.current?.closest(".review-detail-modal")?.classList.add("focus");
        scrollTriggerRef.current = true;
        setScrollTrigger(true);
      }
    } else {
      if (textAreaRef.current) textAreaRef.current.focus();
      commentRef.current?.closest(".review-detail-modal")?.classList.add("focus");
    }
    setIsCommentMode(true);
  };

  // 문서 클릭 시 코멘트 작성 모드 동작/해제 제어
  const documentClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!target.isConnected) return;
    documentClickTarget.current = target;
    const reviewCommentTarget = target.closest(".review-comment-input");
    if (reviewCommentTarget && !target.classList?.contains("comment-submit")) {
      setIsCommentMode(true);
      if (target === textAreaRef.current.parentElement || target.contains(textAreaRef.current)) {
        textAreaRef.current.focus();
        commentRef.current?.closest(".review-detail-modal")?.classList.add("focus");
      }
      return;
    }
    const footer = target.closest(".review-item-footer");
    const modal = target.closest(".modal-wrap");
    if ((footer && target !== footer) || modal || target.classList.contains("comment-submit")) {
      return;
    }
    if (
      textAreaRef.current?.value?.trim() !== "" ||
      commentRef.current.querySelector(".img-preview")
    ) {
      resetCommentStatus({ isCommentMode: false, remainData: true });
    } else {
      resetCommentStatus({ isCommentMode: false, remainData: false });
    }
  };

  // 코멘트 작성 모드 초기화
  const resetCommentStatus = ({
    isCommentMode = false,
    remainData = false,
  }: {
    isCommentMode: boolean;
    remainData?: boolean;
  }) => {
    visualViewportTimer.current && clearTimeout(visualViewportTimer.current);
    visualViewportDefault.current = 0;
    chkVisualViewport.current = false;

    if (!remainData) {
      setSpecificComment(null);
      setSecret(false);
      setAnonymous(false);
      setAnonymousNick("");
      setUpperId(0);
      textAreaRef.current?.style.setProperty("text-indent", "0");
      setValue("");
      setImgFiles([]);
      setImgUpload([]);
      setImgBlobs([]);
    }
    if (!isCommentMode) {
      setIsCommentMode(false);
      if (!textAreaRef.current) return;
      textAreaRef.current?.blur();
      textAreaRef.current?.scrollTo(0, 0);
      commentRef.current?.closest(".review-detail-modal")?.classList.remove("focus");
    }
  };

  // 코멘트 작성
  const writeCommentHandler = async () => {
    const param: {
      content: string;
      secret: 0 | 1;
      uuid: string;
      urls?: string;
      anonymous?: string;
    } = {
      content: value.trim(),
      secret: secret ? 1 : 0,
      uuid: user.me.uuid,
    };
    if (!param.uuid) return;
    if (!param.content) {
      alert("내용을 입력해주세요.");
      return;
    }

    // 공통 옵셔널 항목 (이미지 첨부)
    if ((!imgFiles || !imgFiles[0]) && imgUpload[0]) {
      param.urls = imgUpload[0];
    }
    const type = !specificComment ? "submit" : specificComment.writeType;
    if (type === "delete") return; //삭제는 별도 모달로 처리
    if (type === "submit") {
      //원댓글 작성
      param["reviewUuid"] = reviewDataDetail.reviewData.uuid;
      param["upperId"] = 0;
    }
    if (type === "reply") {
      //대댓글 작성
      param["reviewUuid"] = reviewDataDetail.reviewData.uuid;
      param["upperId"] = upperId;
      param["upperCustomerUuid"] = specificComment.commentData.customerUuid;
    }
    if (anonymous && anonymousNick) {
      param["anonymous"] = anonymousNick;
    }

    let file = imgFiles[0];
    if (imgBlobs && imgBlobs.length > 0) {
      const newBlob = imgBlobs[imgBlobs.length - 1];
      // Blob을 File로 변환하면서 이름 추가
      file = new File([newBlob], "review-comment-img.jpg", {
        type: newBlob.type,
      });
    }
    await writeComment({ param, file, type })
      .then((res) => {
        if (type !== "modify") {
          setReviewDataDetail({
            ...reviewDataDetail,
            reviewData: {
              ...reviewDataDetail.reviewData,
              replyCnt: reviewDataDetail.reviewData.replyCnt + 1,
            },
          });
        }
      })
      .catch((err) => {})
      .finally(() => {
        prevSpecificComment.current = null;
        resetCommentStatus({ isCommentMode: false });
      });
  };

  // 리사이즈 이벤트 핸들러 (키보드 노출 -> 화면 스크롤 트리거 시작, 키보드 숨김 -> 코멘트 모드 종료 처리)
  const viewportResizeEventHandler = () => {
    resizeTimer.current && clearTimeout(resizeTimer.current);
    resizeHeight.current = window.visualViewport.height;
    if (textAreaRef.current !== document.activeElement) return;
    resizeTimer.current = setTimeout(() => {
      if (defaultHeight.current - resizeHeight.current > 200) {
        scrollTriggerRef.current = true;
        setScrollTrigger(true);
      }
      if (defaultHeight.current === resizeHeight.current && documentClickTarget.current) {
        const commentArea = documentClickTarget.current.closest(".review-comment-input");
        const footer = documentClickTarget.current.closest(".review-item-footer");
        const modal = documentClickTarget.current.closest(".modal-wrap");
        if ((footer && documentClickTarget.current !== footer) || modal || commentArea) {
          return;
        }
        resetCommentStatus({ isCommentMode: false, remainData: true });
      }
    }, 50);
  };

  // textarea keydown 이벤트 핸들러 (backspace 두 번 입력 시 코멘트 초기화 처리)
  const textAreaKeyDownHandler = (e: KeyboardEvent) => {
    e.stopPropagation();
    if (
      e.key === "Backspace" &&
      textAreaRefKeyDown.current === "Backspace" &&
      textAreaRef.current.value === ""
    ) {
      resetCommentStatus({
        isCommentMode: true,
        remainData: false,
      });
      textAreaRefKeyDown.current = "";
    } else {
      textAreaRefKeyDown.current = e.key;
    }
  };

  // textarea scroll 타이머
  const textAreaScrollTimer = useRef<NodeJS.Timeout | null>(null);

  // reply-user top 값 추적
  const textAreaTopValue = useRef(0);

  // textarea scroll 이벤트 핸들러 (대댓글 작성 시 reply-user 위치 조정)
  const textAreaScrollHandler = (e: Event) => {
    e.stopPropagation();
    e.preventDefault();
    if (textAreaScrollTimer.current) {
      clearTimeout(textAreaScrollTimer.current);
    }
    const target = e.target as HTMLTextAreaElement;
    const replyUser = target.parentElement.querySelector(".reply-user") as HTMLElement;
    if (!target || !replyUser) return;
    textAreaTopValue.current = Number(replyUser.style.top);
    const scrollTop = target.scrollTop;
    if (scrollTop < -1) {
      replyUser.style.setProperty(
        "transform",
        "translateY(-" + target.scrollHeight + target.clientHeight + "px)",
      );
    } else {
      if (target.scrollHeight - target.clientHeight < scrollTop) {
        replyUser.style.setProperty(
          "transform",
          "translateY(-" + target.scrollHeight + target.clientHeight + "px)",
        );
      } else {
        replyUser.style.setProperty("transform", "translateY(-" + scrollTop + "px)");
      }
    }
  };

  // visualViewport 체크 / visualViewport.offestTop 값 변경 시 visualViewport 체크
  const visualViewportChk = () => {
    if (visualViewportDefault.current !== window.visualViewport.offsetTop) {
      visualViewportDefault.current = window.visualViewport.offsetTop;
      clearTimeout(visualViewportTimer.current);
      chkVisualViewport.current = true;
    }
  };

  // visualViewport 키보드 숨김으로 변경 시 textarea blur 처리, 아닐 경우 visualViewportChk 실행
  const visualViewportHandler = () => {
    if (chkVisualViewport.current && textAreaRef.current) {
      if (visualViewportDefault.current !== window.visualViewport.offsetTop) {
        textAreaRef.current.blur();
        commentRef.current?.closest(".review-detail-modal")?.classList.remove("focus");
      }
    } else {
      visualViewportTimer.current && clearTimeout(visualViewportTimer.current);
      visualViewportTimer.current = setTimeout(visualViewportChk, 100);
    }
  };

  // textarea focus 이벤트 핸들러 (포커스 시 pointer-events 활성화)
  const textAreaFocusHandler = () => {
    visualViewportTimer.current && clearTimeout(visualViewportTimer.current);
    visualViewportDefault.current = 0;
    chkVisualViewport.current = false;
  };

  const windowFocusHandler = () => {
    if (imgUploadFlag.current) {
      commentActiveHandler({ needToMove: true, isCommentMode: true });
      setTimeout(() => {
        commentRef.current?.closest(".review-detail-modal")?.classList.remove("focus");
      }, 300);
    }
    imgUploadFlag.current = false;
  };

  const textAreaBlurHandler = () => {};

  // 최초 렌더링 시 리사이즈 이벤트 등록 / 문서 클릭 이벤트 등록 / textarea keydown 이벤트 등록
  useEffect(() => {
    defaultHeight.current = window.visualViewport.height;

    // 리사이즈 이벤트 등록
    window.visualViewport.addEventListener("resize", viewportResizeEventHandler);
    // 이미지 파일 선택 후 기존 창으로 포커스 돌아왔을 때 처리
    window.addEventListener("focus", windowFocusHandler);

    // 문서 클릭 이벤트 등록, textarea keydown 이벤트 등록
    if (textAreaRef.current) {
      // textAreaRef.current.style.pointerEvents = "none";
      document.documentElement.addEventListener("click", documentClick);
      textAreaRef.current.addEventListener("keydown", (e) => textAreaKeyDownHandler(e));
      textAreaRef.current.addEventListener("scroll", textAreaScrollHandler);
      window.visualViewport.addEventListener("scroll", () => {
        visualViewportHandler();
      });
      textAreaRef.current.addEventListener("focus", textAreaFocusHandler);
      textAreaRef.current.addEventListener("blur", textAreaBlurHandler);
    }

    return () => {
      window.visualViewport.removeEventListener("resize", viewportResizeEventHandler);
      window.removeEventListener("focus", windowFocusHandler);
      document.documentElement.removeEventListener("click", documentClick);
      if (textAreaRef.current) {
        textAreaRef.current.removeEventListener("keydown", (e) => textAreaKeyDownHandler(e));
        textAreaRef.current?.removeEventListener("scroll", textAreaScrollHandler);
        window.visualViewport.removeEventListener("scroll", () => {
          visualViewportHandler();
        });
        textAreaRef.current.removeEventListener("focus", textAreaFocusHandler);
        textAreaRef.current.removeEventListener("blur", textAreaBlurHandler);
      }
      resetCommentStatus({ isCommentMode: false });
    };
  }, []);

  useEffect(() => {
    isCommentModeCheck(isCommentMode);
  }, [isCommentMode]);

  // 댓글창 높이 조절
  useEffect(() => {
    if (commentRef.current) {
      setCommentHeight(commentRef.current.scrollHeight);
      ro.observe(commentRef.current);
    }
    return () => {
      ro.disconnect();
    };
  }, [commentRef]);

  // 익명 닉네임 처리 (수정+기존익명인 경우 그대로 기존 익명 닉네임 사용, 새로 익명 닉네임 필요한 경우에는 api 호출)
  useEffect(() => {
    if (anonymous) {
      if (!user.me.uuid) return;
      if (anonymousNick) return;
      if (
        specificComment &&
        specificComment.writeType === "modify" &&
        specificComment.commentData.anonymous === 1
      ) {
        setAnonymousNick(specificComment.commentData.nick || "");
      } else {
        getAnonymousNick(user.me.uuid).then((res) => {
          setAnonymousNick(res.data.item.nick);
        });
      }
    } else {
      setAnonymousNick("");
    }
  }, [anonymous, user?.me?.uuid, anonymousNick]);

  // 스크롤 트리거 발동 시 특정 댓글 위치로 이동
  const prevPosition = useRef<number>(0);
  useEffect(() => {
    if (scrollTrigger && scrollTriggerRef.current) {
      if (specificComment?.target) {
        const containerElement = scrollComponent?.contents;
        const boundingRect = specificComment.target.getBoundingClientRect();

        let position =
          boundingRect.top -
          containerElement?.getBoundingClientRect().top +
          containerElement?.scrollTop -
          (window.visualViewport.height -
            containerElement.getBoundingClientRect().top -
            commentHeight -
            boundingRect.height);

        if (prevPosition.current !== position) {
          containerElement.scrollTo({
            top: position,
            // behavior: "smooth",
          });
        } else {
          containerElement.scrollTo({
            top: position,
            // behavior: "smooth",
          });
        }

        prevPosition.current = position;
      }
      scrollTriggerRef.current = false;
      setScrollTrigger(false);
      setTimeout(() => {
        if (textAreaRef.current) {
          textAreaRef.current.focus();
          commentRef.current?.closest(".review-detail-modal")?.classList.add("focus");
        }
      }, 100);
    }
  }, [scrollTrigger]);

  // 수정, 삭제, 댓글 등 특정 댓글 기반 작성 모드 세팅
  const prevSpecificComment = useRef<{
    id: number;
    writeType: TWriteType;
    commentData: ICommunityCommentListItem | null;
    target: HTMLElement | null;
  } | null>(null);
  useEffect(() => {
    if (specificComment) {
      if (specificComment.writeType === "delete") {
        // 아래는 댓글 보여주기용
        setIsCommentMode(true);
        setValue(specificComment.commentData.content);
        setSecret(specificComment.commentData.secret === 1);
        setAnonymous(specificComment.commentData.anonymous === 1);
        setImgUpload(
          specificComment.commentData.images[0] ? [specificComment.commentData.images[0].url] : [],
        );
      } else {
        if (
          prevSpecificComment?.current &&
          prevSpecificComment.current?.commentData?.uuid === specificComment.commentData?.uuid &&
          prevSpecificComment.current?.writeType === specificComment.writeType &&
          textAreaRef.current?.value.trim().length > 0
        ) {
          // 이전 댓글과 같은 댓글 클릭 + 같은 모드일때 + 현재 입력 내용이 있을 때는 내용 바꾸지 않도록
        } else {
          if (specificComment.writeType === "modify") {
            setValue(specificComment.commentData.content);
            setSecret(specificComment.commentData.secret === 1);
            setAnonymous(specificComment.commentData.anonymous === 1);
            setAnonymousNick(specificComment.commentData.nick || "");
            setUpperId(specificComment.commentData.upperId || 0);
            setImgUpload(
              specificComment.commentData.images[0]
                ? [specificComment.commentData.images[0].url]
                : [],
            );
          } else {
            setValue("");
            setSecret(false);
            setAnonymous(false);
            setUpperId(specificComment.commentData.id);
            setImgUpload([]);
          }
        }

        if (
          prevSpecificComment.current?.commentData?.uuid === specificComment.commentData?.uuid &&
          prevSpecificComment.current?.writeType === specificComment.writeType
        ) {
          if (isCommentMode) {
            commentActiveHandler({ needToMove: false });
          } else {
            commentActiveHandler({ needToMove: true });
          }
        } else {
          if (isCommentMode) {
            commentActiveHandler({ needToMove: true, isCommentMode: true });
          } else {
            commentActiveHandler({ needToMove: true, isCommentMode: false });
          }
        }
      }

      if (
        specificComment.commentData?.upperNick ||
        (specificComment.writeType === "reply" && !specificComment.commentData?.upperNick)
      ) {
        textAreaRef.current?.style.setProperty(
          "text-indent",
          textAreaRef.current.parentElement.querySelector(".reply-user").clientWidth + 8 + "px",
        );
      } else {
        textAreaRef.current?.style.setProperty("text-indent", "0");
      }

      prevSpecificComment.current = specificComment;
    }
  }, [specificComment && specificComment.id]);

  useEffect(() => {
    setTimeout(() => {
      scrollComponent?.calcProperties();
    }, 300);
  }, [commentHeight]);

  return (
    <>
      {specificComment?.writeType === "delete" && (
        <Modal
          isShow={true}
          overlay={true}
          overlayValue={0.5}
          overlayStyle={{ zIndex: 99998 }}
          text={`댓글을 삭제하시겠습니까?
          삭제 후에는 복구가 불가합니다.`}
          buttons={[
            {
              name: "아니요",
              className: "btn-gray50",
              action: () => {
                resetCommentStatus({ isCommentMode: false });
              },
            },
            {
              name: "삭제",
              className: "btn-red500",
              action: () => {
                const param = {
                  uuid: user.me.uuid,
                };
                deleteCommunityComment(specificComment.commentData.uuid, param)
                  .then((res) => {
                    const newComment = commentList.map((comment) => {
                      if (comment.uuid === specificComment.commentData.uuid) {
                        return { ...comment, state: 0 };
                      }
                      return comment;
                    });
                    setCommentList(newComment);
                  })
                  .catch((err) => {
                    alert("댓글 삭제에 실패했습니다. 잠시 후 다시 시도해주세요.");
                  })
                  .finally(() => {
                    resetCommentStatus({ isCommentMode: false });
                  });
              },
            },
          ]}
        />
      )}
      <div
        className={`review-comment-input prevent-scroll ${isCommentMode ? "is-comment-mode" : ""} ${isiOS ? "ios" : ""}`}
        ref={commentRef}
      >
        <Blank height={8} color={"#f7f7f7"} /> {/* gray30 */}
        <ImgProvider
          type="button"
          image={imgUpload}
          setImage={setImgUpload}
          iconSrc={IconCommentImg}
          uniqueKey="review-comment-img"
          file={imgFiles}
          setFile={setImgFiles}
          maxLength={1}
          blob={imgBlobs}
          setBlob={setImgBlobs}
        >
          {isCommentMode && (
            <>
              <div className="comment-types">
                <ul>
                  <li>
                    <Checkbox
                      size="small"
                      id="review-comment-anonymous"
                      name="review-comment-anonymous"
                      value="1"
                      checked={anonymous}
                      setValue={() => {
                        if (!anonymous) {
                          setSecret(false);
                        }
                        setAnonymous((prev) => !prev);
                      }}
                      labelComponent={
                        <>
                          <img src={IconCommentAnonymous} alt="댓글 익명 작성 선택" width={18} />
                          익명글
                        </>
                      }
                    />
                  </li>
                  <li>
                    <Checkbox
                      size="small"
                      id="review-comment-secret"
                      name="review-comment-secret"
                      value="2"
                      checked={secret}
                      setValue={() => {
                        if (!secret) {
                          setAnonymous(false);
                        }
                        setSecret((prev) => !prev);
                      }}
                      labelComponent={
                        <>
                          <img src={IconCommentLock} alt="댓글 비밀 작성 선택" width={18} />
                          비밀글
                        </>
                      }
                    />
                  </li>
                </ul>
              </div>
              <ImgPreview />
            </>
          )}
          <div className="comment-inputs">
            <button className="btn btn-icon upload-image">
              <ImgUpload
                maxWidth={1000}
                quality={0.9}
                mimeType={"image/jpeg"}
                onClick={(valid) => {
                  if (valid) {
                    imgUploadFlag.current = true;
                  } else {
                    imgUploadFlag.current = false;
                  }
                }}
              />
            </button>
            <div className="comment-textarea">
              {specificComment && specificComment.writeType === "reply" && (
                <span className="reply-user">@{specificComment.commentData.nick}</span>
              )}
              {specificComment &&
                specificComment.writeType !== "reply" &&
                specificComment.commentData?.depth === 2 && (
                  <span className="reply-user">@{specificComment.commentData.upperNick}</span>
                )}
              <AutoResizeTextArea
                rows={1}
                cols={0}
                value={value}
                setValue={setValue}
                ref={textAreaRef}
              />
            </div>
            <button
              className="btn btn-auto-width btn-no-padding btn-giant bold comment-submit"
              onClick={writeCommentHandler}
            >
              입력
            </button>
          </div>
        </ImgProvider>
      </div>
      {/* <Blank height={commentHeight} color={"#f7f7f7"} /> */}
      {/* {isiOS && <div className="blank-wrap"></div>} */}
      {/*댓글창 높이만큼 공간 확보*/}
    </>
  );
};
